import React, {useEffect, useState} from 'react';
import {Dialog, FormControl, MenuItem, Select} from '@material-ui/core';
import {useDispatch, useSelector} from 'react-redux';
import moment from 'moment';
import {toast} from 'react-toastify';
import DialogBody from 'components/DialogBody/styles';
import DialogTitle from 'components/DialogTitle/styles';
import DialogContainer from 'components/DialogContainer/styles';
import DialogActions from 'components/DialogActions/styles';
import DialogAction from 'components/DialogAction/styles';
import {ButtonOutline, ButtonPrimary} from 'components/buttons';
import FieldInput from 'components/FieldInput';
import {FieldInputDollar} from 'routes/App/dashboard/styles';
import SelectWrapper from 'components/select/SelectComponentStyles';
import Loading from 'components/shared/Utils/Loading';
import {
  CREATE_LARGE_ORDER_DRIVERS,
  REMOVE_LARGE_ORDER
} from 'store/global/ordersV2/action';
import {ORDER} from 'helpers/constants/orders_v2';
import {BOOK_A_DRIVER_ERROR} from 'helpers/constants/bookAdriver';
import {shouldBlockBookADriverForClosedLocation} from 'helpers/functionUtils/bookADriver';
import Body1 from 'components/typography/Body1';
import {
  LARGE_DELIVERY_DEFAULT_DRIVERS,
  LARGE_DELIVERY_DEFAULT_TIP,
  LARGE_DELIVERY_MAX_DRIVERS,
  LARGE_DELIVERY_MIN_DRIVERS,
  LARGE_DELIVERY_TIP_CUSTOM,
  LARGE_DELIVERY_TIP_OPTIONS
} from 'helpers/constants/largeDelivery';
import {groupDigital} from 'helpers/functionUtils/groupByDigital';
import {
  calculateLargeDeliveryTotal,
  getLargeDeliveryDefaultTipAmount,
  getLargeDeliveryFee,
  getTotalItemsCount
} from 'helpers/functionUtils/ordersUtilsV2';
import {
  LargeDeliveryControlLabel,
  LargeDeliveryDivider,
  LargeDeliveryLabel,
  LargeDeliveryLoading,
  LargeDeliveryRow,
  LargeDeliverySection,
  LargeDeliveryStepper,
  LargeDeliveryStepperButton,
  LargeDeliveryStepperValue,
  LargeDeliverySubtitle,
  LargeDeliveryTipAmount,
  LargeDeliveryTipNote,
  LargeDeliveryTipSelect,
  LargeDeliveryTotalLabel,
  LargeDeliveryTotalRow,
  LargeDeliveryTotalValue,
  LargeDeliveryValue
} from './styles';

const formatMoney = (value) =>
  `$${groupDigital(Number(value || 0).toFixed(2))}`;

const formatEta = (order, timezone) => {
  if (!order?.[ORDER.DATE]) {
    return 'N/A';
  }
  const eta = moment(`${order[ORDER.DATE]}Z`).utcOffset(0);
  const locationNow = moment().utcOffset(Number(timezone) || 0);
  if (eta.isSame(locationNow, 'day')) {
    return `Today, ${eta.format('h:mm A')}`;
  }
  if (eta.isSame(locationNow.clone().add(1, 'day'), 'day')) {
    return `Tomorrow, ${eta.format('h:mm A')}`;
  }
  return eta.format('MMM D, h:mm A');
};

const formatTipAmountInput = (value, previousValue = '') => {
  if (value === '') {
    return '';
  }
  const normalized = String(value).replace(',', '.');
  if (!/^\d*\.?\d{0,2}$/.test(normalized)) {
    return previousValue;
  }
  if (Number(normalized) > 10000) {
    return previousValue;
  }
  return normalized;
};

const LargeDeliveryModal = () => {
  const dispatch = useDispatch();
  const {largeOrders, largeOrderSubmitting, newOrders} = useSelector(
    (state) => state.activeOrdersV2Reducer
  );
  const openSuccessModal = useSelector(
    (state) => state.bookADriverCreateReducer.openSuccessModal
  );
  const activeLocation = useSelector((state) => state.user?.activeLocation);
  const timezone = activeLocation?.Timezone;
  const isClosedForBookADriver = shouldBlockBookADriverForClosedLocation(
    activeLocation
  );
  const currentOrder = largeOrders?.[0];
  const [additionalDrivers, setAdditionalDrivers] = useState(
    LARGE_DELIVERY_DEFAULT_DRIVERS
  );
  const [tip, setTip] = useState(LARGE_DELIVERY_DEFAULT_TIP);
  const [customTipAmount, setCustomTipAmount] = useState('0');

  const defaultTipAmount = getLargeDeliveryDefaultTipAmount(currentOrder);

  useEffect(() => {
    setAdditionalDrivers(LARGE_DELIVERY_DEFAULT_DRIVERS);
    setTip(LARGE_DELIVERY_DEFAULT_TIP);
    setCustomTipAmount('0');
  }, [currentOrder?.[ORDER.ID]]);

  useEffect(() => {
    if (defaultTipAmount == null) {
      return;
    }
    setTip(LARGE_DELIVERY_TIP_CUSTOM);
    setCustomTipAmount(
      Number.isInteger(defaultTipAmount)
        ? String(defaultTipAmount)
        : defaultTipAmount.toFixed(2)
    );
  }, [currentOrder?.[ORDER.ID], defaultTipAmount]);

  if (!currentOrder || openSuccessModal || newOrders?.length) {
    return null;
  }

  const isInfoLoading = Boolean(currentOrder.largeDeliveryLoading);
  const isBusy = largeOrderSubmitting || isInfoLoading;
  const itemsCount = getTotalItemsCount(currentOrder[ORDER.CART]?.items);
  const subtotal = Number(currentOrder[ORDER.TOTALS]?.locationSubtotal || 0);
  const customerName = currentOrder[ORDER.CUSTOMER]?.fullName || 'N/A';
  const deliveryFee = getLargeDeliveryFee(currentOrder);
  const total = calculateLargeDeliveryTotal({
    deliveryFee,
    additionalDrivers,
    tip,
    customTipAmount,
    subtotal
  });

  const onClose = () => {
    if (largeOrderSubmitting) {
      return;
    }
    dispatch(REMOVE_LARGE_ORDER(currentOrder[ORDER.ID]));
  };

  const onAddDrivers = () => {
    if (isBusy) {
      return;
    }
    if (isClosedForBookADriver) {
      toast.error(BOOK_A_DRIVER_ERROR.LOCATION_CLOSED);
      return;
    }
    dispatch(
      CREATE_LARGE_ORDER_DRIVERS({
        orderId: currentOrder[ORDER.ID],
        additionalDrivers,
        tip,
        customTipAmount:
          tip === LARGE_DELIVERY_TIP_CUSTOM ? customTipAmount : 0,
        total
      })
    );
  };

  return (
    <Dialog open scroll='body' onClose={onClose}>
      <DialogContainer className='DialogSm2'>
        <DialogTitle>You have a large delivery order</DialogTitle>
        <LargeDeliverySubtitle>
          Consider adding additional drivers to avoid delivery issues
        </LargeDeliverySubtitle>
        <DialogBody>
          <LargeDeliveryDivider />
          <LargeDeliverySection>
            <LargeDeliveryRow>
              <LargeDeliveryLabel>Items</LargeDeliveryLabel>
              <LargeDeliveryValue>{itemsCount}</LargeDeliveryValue>
            </LargeDeliveryRow>
            <LargeDeliveryRow>
              <LargeDeliveryLabel>Subtotal</LargeDeliveryLabel>
              <LargeDeliveryValue>{formatMoney(subtotal)}</LargeDeliveryValue>
            </LargeDeliveryRow>
            <LargeDeliveryRow>
              <LargeDeliveryLabel>Customer</LargeDeliveryLabel>
              <LargeDeliveryValue>{customerName}</LargeDeliveryValue>
            </LargeDeliveryRow>
            <LargeDeliveryRow>
              <LargeDeliveryLabel>ETA</LargeDeliveryLabel>
              <LargeDeliveryValue>
                {formatEta(currentOrder, timezone)}
              </LargeDeliveryValue>
            </LargeDeliveryRow>
          </LargeDeliverySection>
          <LargeDeliveryDivider />
          {isInfoLoading ? (
            <LargeDeliveryLoading>
              <Loading className='LoadingWrapperCenter' />
            </LargeDeliveryLoading>
          ) : (
            <>
              <LargeDeliverySection>
                <LargeDeliveryRow>
                  <LargeDeliveryControlLabel>
                    How many additional drivers?
                  </LargeDeliveryControlLabel>
                  <LargeDeliveryStepper>
                    <LargeDeliveryStepperButton
                      type='button'
                      disabled={
                        additionalDrivers <= LARGE_DELIVERY_MIN_DRIVERS ||
                        largeOrderSubmitting
                      }
                      onClick={() =>
                        setAdditionalDrivers((value) =>
                          Math.max(LARGE_DELIVERY_MIN_DRIVERS, value - 1)
                        )
                      }
                    >
                      −
                    </LargeDeliveryStepperButton>
                    <LargeDeliveryStepperValue>
                      {additionalDrivers}
                    </LargeDeliveryStepperValue>
                    <LargeDeliveryStepperButton
                      type='button'
                      disabled={
                        additionalDrivers >= LARGE_DELIVERY_MAX_DRIVERS ||
                        largeOrderSubmitting
                      }
                      onClick={() =>
                        setAdditionalDrivers((value) =>
                          Math.min(LARGE_DELIVERY_MAX_DRIVERS, value + 1)
                        )
                      }
                    >
                      +
                    </LargeDeliveryStepperButton>
                  </LargeDeliveryStepper>
                </LargeDeliveryRow>
                <LargeDeliveryRow>
                  <LargeDeliveryControlLabel>
                    Drivers&apos; tip
                  </LargeDeliveryControlLabel>
                  <LargeDeliveryTipSelect>
                    <SelectWrapper className='SelectMd w100'>
                      <FormControl className='select-formcontrol'>
                        <Select
                          value={tip}
                          name='DriversTip'
                          disabled={largeOrderSubmitting}
                          onChange={(event) => {
                            const nextTip = event.target.value;
                            setTip(nextTip);
                            if (
                              nextTip === LARGE_DELIVERY_TIP_CUSTOM &&
                              customTipAmount === ''
                            ) {
                              setCustomTipAmount('0');
                            }
                          }}
                          MenuProps={{
                            anchorOrigin: {
                              vertical: 'bottom',
                              horizontal: 'left'
                            },
                            transformOrigin: {
                              vertical: 'top',
                              horizontal: 'left'
                            },
                            getContentAnchorEl: null
                          }}
                        >
                          {LARGE_DELIVERY_TIP_OPTIONS.map((option) => (
                            <MenuItem key={option.label} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </SelectWrapper>
                  </LargeDeliveryTipSelect>
                </LargeDeliveryRow>
                <LargeDeliveryTipNote>
                  <span>*</span> The tip will be distributed evenly among all
                  drivers.
                </LargeDeliveryTipNote>
                {tip === LARGE_DELIVERY_TIP_CUSTOM && (
                  <LargeDeliveryRow>
                    <LargeDeliveryControlLabel>
                      Tip amount
                    </LargeDeliveryControlLabel>
                    <LargeDeliveryTipAmount>
                      <FieldInputDollar>
                        <FieldInput
                          name='CustomTipAmount'
                          value={customTipAmount}
                          disabled={largeOrderSubmitting}
                          onChange={(event) =>
                            setCustomTipAmount(
                              formatTipAmountInput(
                                event.target.value,
                                customTipAmount
                              )
                            )
                          }
                        />
                      </FieldInputDollar>
                    </LargeDeliveryTipAmount>
                  </LargeDeliveryRow>
                )}
              </LargeDeliverySection>
              <LargeDeliveryDivider />
              <LargeDeliveryTotalRow>
                <LargeDeliveryTotalLabel>
                  Total (delivery fee + tip)
                </LargeDeliveryTotalLabel>
                <LargeDeliveryTotalValue>
                  {formatMoney(total)}
                </LargeDeliveryTotalValue>
              </LargeDeliveryTotalRow>
            </>
          )}
          {isClosedForBookADriver && (
            <Body1 className='RedText marginBottomSm'>
              {BOOK_A_DRIVER_ERROR.LOCATION_CLOSED}
            </Body1>
          )}
          <DialogActions className='DialogActionsNoWrap'>
            <DialogAction className='w50'>
              <ButtonOutline
                className={largeOrderSubmitting ? 'disabled' : ''}
                onClick={onClose}
              >
                Cancel
              </ButtonOutline>
            </DialogAction>
            <DialogAction className='w50'>
              <ButtonPrimary
                className={isBusy ? 'disabled' : ''}
                onClick={onAddDrivers}
              >
                <span>Add drivers</span>
              </ButtonPrimary>
            </DialogAction>
          </DialogActions>
        </DialogBody>
      </DialogContainer>
    </Dialog>
  );
};

export default LargeDeliveryModal;
